import getProductCategoriesApi from '@/api/get/company/get-product-categories';
import { Col, Row } from '@/components/grid/grid-component';
import FieldWithRevert from '@/components/react-hook-form-inputs/field-with-revert/field-with-revert-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import RichTextEditorInput from '@/components/react-hook-form-inputs/rich-text-input/rich-text-editor';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import { useQuery } from '@/hooks/use-api';
import {
  requiredValidationDropdownMessage,
  requiredValidationMessage,
} from '@/utils/validations';
import PropTypes from 'prop-types';
import { Fragment, useContext } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Company product details form
 * @param {object} props - The context to use.
 * @param {object} props.CONTEXT - The context to use.
 * @param {boolean} props.isTemplate - Whether the form is for a template.
 * @param {Function} props.handleRevert - The function to handle the revert.
 * @param {Function} props.handleSingleSelectChange - The function to handle the single select change.
 * @param {Function} props.createRichTextBlurHandler - The function to create the rich text blur handler.
 * @returns {JSX.Element}
 */
const ProductDetailsForm = ({
  CONTEXT,
  isTemplate,
  handleSingleSelectChange,
  createRichTextBlurHandler,
  handleRevert,
}) => {
  const { id } = useParams();
  const { prices } = useContext(CONTEXT);
  const { data: productCategories } = useQuery(getProductCategoriesApi, id);

  const Revert = isTemplate ? Fragment : FieldWithRevert;

  return (
    <>
      <Row>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Input
            type="text"
            name={isTemplate ? 'template.name' : 'product.name'}
            label={isTemplate ? 'Template name' : 'Product name'}
            placeholder={isTemplate ? 'Template name' : 'Product name'}
            checked
            onChange={() => {}}
            disabled={false}
            registerOptions={{
              required: requiredValidationMessage,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Revert
            fieldName="product.price_id"
            tooltipMessage="Revert Part Number"
            right="5px"
            top="23px"
            handleRevert={handleRevert}
          >
            <SelectSearch
              header="Part Number"
              name={isTemplate ? 'template.price_id' : 'product.price_id'}
              label="Please select a part number"
              options={prices?.map((c) => ({
                label: c.part_number,
                value: c.id,
              }))}
              required={{
                required: requiredValidationDropdownMessage,
              }}
              onChange={(value) =>
                handleSingleSelectChange(
                  isTemplate ? 'template.price_id' : 'product.price_id',
                  value,
                )
              }
            />
          </Revert>
        </Col>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Revert
            fieldName="product.product_category_id"
            tooltipMessage="Revert Product Category"
            right="5px"
            top="23px"
            handleRevert={handleRevert}
          >
            <SelectSearch
              header="Product Category"
              name={
                isTemplate
                  ? 'template.product_category_id'
                  : 'product.product_category_id'
              }
              label="Please select a product category"
              options={productCategories?.map((c) => ({
                label: c.name,
                value: c.id,
              }))}
              required={{
                required: requiredValidationDropdownMessage,
              }}
              onChange={(value) =>
                handleSingleSelectChange(
                  isTemplate
                    ? 'template.product_category_id'
                    : 'product.product_category_id',
                  value,
                )
              }
            />
          </Revert>
        </Col>
      </Row>

      <Row>
        <Col sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Revert
            fieldName="product.description"
            tooltipMessage="Revert Description"
            handleRevert={handleRevert}
          >
            <RichTextEditorInput
              label={
                isTemplate ? 'Template Description' : 'Product Description'
              }
              name={isTemplate ? 'template.description' : 'product.description'}
              view={false}
              newVersion
              marginBottom="25px"
              rules={{
                required: requiredValidationMessage,
              }}
              onBlur={createRichTextBlurHandler('product.description')}
            />
          </Revert>
        </Col>
      </Row>
    </>
  );
};
ProductDetailsForm.defaultProps = {
  isTemplate: false,
  handleSingleSelectChange: () => {},
  createRichTextBlurHandler: () => {},
  handleRevert: () => {},
};

ProductDetailsForm.propTypes = {
  CONTEXT: PropTypes.object.isRequired,
  isTemplate: PropTypes.bool,
  handleSingleSelectChange: PropTypes.func,
  createRichTextBlurHandler: PropTypes.func,
  handleRevert: PropTypes.func,
};

export default ProductDetailsForm;
